import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

type ActionStatus =
  | "none"
  | "watching"
  | "authorized"
  | "submitted"
  | "removed"
  | "rejected"
  | string;

type StatusUpdate = {
  actionStatus: string;
  submittedAt: string | null;
  removedAt: string | null;
  chargeStatus?: string | null;
};

export function TakedownStatusControls({
  reviewId,
  actionStatus,
  onChange,
  className = "",
}: {
  reviewId: string;
  actionStatus: ActionStatus;
  onChange: (next: StatusUpdate) => void;
  className?: string;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (actionStatus !== "authorized") return null;

  async function cancelAuthorization() {
    setError(null);
    setSaving(true);
    const res = await api<{ classification: StatusUpdate }>(
      `/customer/reviews/${reviewId}/action-status`,
      {
        method: "PATCH",
        body: JSON.stringify({ actionStatus: "none" }),
      }
    );
    setSaving(false);
    if (!res.ok || !res.data?.classification) {
      setError(res.error ?? "Could not update status.");
      return;
    }
    onChange(res.data.classification);
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={cancelAuthorization}
        disabled={saving}
        className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 disabled:opacity-60 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        {saving
          ? "Saving..."
          : "Cancel authorization"}
      </button>
      {error && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
